// Admin → central impersonation state (roadmap 2.4).
// The admin token/user are stashed away while a central token takes their place,
// so the whole app behaves as the central; an AppShell banner offers the way back.
import type { AuthUser } from "./auth";
import { getStoredToken, getStoredUser, persistAuth } from "./auth";

const ADMIN_BACKUP_KEY = "sij_imp_admin";
const IMPERSONATION_KEY = "sij_impersonating";

export interface ImpersonationState {
  central_id: number;
  central_name: string;
}

export function getImpersonation(): ImpersonationState | null {
  const raw = localStorage.getItem(IMPERSONATION_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as ImpersonationState;
  } catch {
    return null;
  }
}

export async function startImpersonation(central: { id: number; name: string }): Promise<void> {
  const adminToken = getStoredToken();
  const adminUser = getStoredUser();
  if (!adminToken || !adminUser) throw new Error("Not authenticated");

  const { loginAsCentral } = await import("./queries");
  const res = await loginAsCentral(central.id);
  const token = res.token;
  const user = res.user;

  localStorage.setItem(ADMIN_BACKUP_KEY, JSON.stringify({ token: adminToken, user: adminUser }));
  localStorage.setItem(IMPERSONATION_KEY, JSON.stringify({ central_id: central.id, central_name: central.name }));
  persistAuth(token, user);
}

export async function stopImpersonation(): Promise<void> {
  const state = getImpersonation();
  if (state) {
    try {
      const { logoutAsCentral } = await import("./queries");
      await logoutAsCentral(state.central_id);
    } catch {
      /* best-effort server-side cleanup */
    }
  }
  const raw = localStorage.getItem(ADMIN_BACKUP_KEY);
  localStorage.removeItem(ADMIN_BACKUP_KEY);
  localStorage.removeItem(IMPERSONATION_KEY);
  if (raw) {
    try {
      const backup = JSON.parse(raw) as { token: string; user: AuthUser };
      persistAuth(backup.token, backup.user);
      return;
    } catch {
      /* fall through */
    }
  }
}
